import { z } from "zod";
import { evidenceKeySchema } from "./contribution-evidence.js";

/* ===========================================================================
 * US-E16.F5-0186 (@UC9245–@UC9248) — Thu đặc thù (penalty/usage/by_area) + GHI INCOME (wire 3
 * txType chết: SPONSORSHIP_MEMBER / SPONSORSHIP_EXTERNAL / OTHER_INCOME).
 * Phase Post-MVP — @gate money,ledger 🔴.
 *
 * INVARIANTS:
 *  - B1 tiền BigInt: MỌI amount = STRING minor (regex ^\d+$, >0, ≤ trần PG BIGINT). KHÔNG float.
 *  - C5 `.strict()` chặn over-post (currency/fundId/kind server set); superRefine chốt field đúng kind.
 *  - Map incomeType → txType là HẰNG export (một nguồn sự thật) — api KHÔNG tự nối chuỗi (chống drift).
 * ========================================================================= */

/** Số nguyên STRING (B1) — không dấu chấm, không âm, không số mũ. */
const INTEGER_STRING_PATTERN = /^\d+$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/** Trần PG signed BIGINT (chống overflow P2020→500). */
const PG_BIGINT_MAX = 9223372036854775807n;
const AMOUNT_MAX_DIGITS = 19;
const TITLE_MAX = 100;
const REASON_MAX = 200;
const SOURCE_REF_MAX = 200;
const NOTE_MAX = 500;
const MAX_ENTRIES = 1000;
/** Trần trọng số/số buổi (chống payload phi lý; weight = m² hoặc số buổi — số nguyên nhỏ). */
const MAX_WEIGHT = 1_000_000;

export const INCOME_AMOUNT_INVALID_MESSAGE = "Số tiền phải là số nguyên dương (đơn vị nhỏ nhất)";

/** Tiền STRING minor > 0, ≤ trần BIGINT (B1). */
/** US-E6.F3-0913 — export cho lời khai Quỹ chung (`general-fund-claim.ts`): khoản đó khi duyệt đi đúng đường income ⇒ một luật biên. */
export const amountMinorString = z
  .string()
  .trim()
  .min(1, INCOME_AMOUNT_INVALID_MESSAGE)
  .max(AMOUNT_MAX_DIGITS, INCOME_AMOUNT_INVALID_MESSAGE)
  .regex(INTEGER_STRING_PATTERN, INCOME_AMOUNT_INVALID_MESSAGE)
  .refine((v) => {
    try {
      const n = BigInt(v);
      return n > 0n && n <= PG_BIGINT_MAX;
    } catch {
      return false;
    }
  }, INCOME_AMOUNT_INVALID_MESSAGE);

function isRealCalendarDate(value: string): boolean {
  const [y, m, d] = value.split("-").map((p) => Number.parseInt(p, 10));
  if (y === undefined || m === undefined || d === undefined) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

const memberIdField = z.string().uuid();
const titleField = z.string().trim().min(1).max(TITLE_MAX);
const dueDateField = z.string().trim().regex(ISO_DATE_PATTERN).refine(isRealCalendarDate);

/* ---------------------------------------------------------------------------
 * S02 — RECORD INCOME (wire dead txType) · @UC9248
 * ------------------------------------------------------------------------- */

/**
 * 3 loại thu NGOÀI nghĩa vụ đóng góp (COL-EXTERNAL). Quyết định nghiệp vụ (financial-principles):
 *  - sponsorship_member   : MEMBER trong nhóm tài trợ THÊM (ngoài nghĩa vụ) — có TÊN member.
 *  - sponsorship_external : nhà tài trợ NGOÀI nhóm (không phải member) — chỉ có sourceRef.
 *  - other_income         : lãi ngân hàng / hoàn phí / thu khác — không gắn member.
 */
export const INCOME_TYPE = {
  SPONSORSHIP_MEMBER: "sponsorship_member",
  SPONSORSHIP_EXTERNAL: "sponsorship_external",
  OTHER_INCOME: "other_income",
} as const;

export type IncomeType = (typeof INCOME_TYPE)[keyof typeof INCOME_TYPE];

/**
 * Map 1-1 incomeType → `txType` ledger (giá trị PHẢI nằm trong TX_TYPES @goppy/domain + CHECK DB).
 * HẰNG export — api reuse (KHÔNG tự nối chuỗi/uppercase ⇒ chống lệch chuỗi âm thầm).
 */
export const INCOME_TYPE_TO_TX_TYPE = {
  [INCOME_TYPE.SPONSORSHIP_MEMBER]: "SPONSORSHIP_MEMBER",
  [INCOME_TYPE.SPONSORSHIP_EXTERNAL]: "SPONSORSHIP_EXTERNAL",
  [INCOME_TYPE.OTHER_INCOME]: "OTHER_INCOME",
} as const satisfies Record<IncomeType, string>;

export type IncomeTxType = (typeof INCOME_TYPE_TO_TX_TYPE)[IncomeType];

/**
 * POST /v1/funds/:id/income — treasurer/owner ghi khoản thu ngoài nghĩa vụ.
 *  - sponsorship_member BẮT BUỘC memberId (member tài trợ phải có TÊN — minh bạch).
 *  - sponsorship_external / other_income CẤM memberId (không phải thu của member).
 *  - fundId/currency CỐ Ý VẮNG (từ :id param / kế thừa fund).
 */
export const recordIncomeRequestSchema = z
  .object({
    incomeType: z.enum([
      INCOME_TYPE.SPONSORSHIP_MEMBER,
      INCOME_TYPE.SPONSORSHIP_EXTERNAL,
      INCOME_TYPE.OTHER_INCOME,
    ]),
    amountMinor: amountMinorString,
    /** groupMember.id — CHỈ với sponsorship_member (service validate ACTIVE cùng group fund). */
    memberId: memberIdField.optional(),
    /** Nguồn thu (tên nhà tài trợ / "Lãi ngân hàng Q2" …) — hiển thị minh bạch. */
    sourceRef: z.string().trim().min(1).max(SOURCE_REF_MAX).optional(),
    note: z.string().trim().max(NOTE_MAX).optional(),
    /** Bắt buộc — scope idempotency (double-submit ⇒ 1 post). */
    idempotencyKey: z.string().trim().min(1).max(200),
    /** 🔴 US-E6.F3-0910 (@UC0057) — khoá file bằng chứng (upload hai bước). KHÔNG đi vào khoá idempotency. */
    evidenceKey: evidenceKeySchema.optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    const hasMember = val.memberId !== undefined;
    if (val.incomeType === INCOME_TYPE.SPONSORSHIP_MEMBER && !hasMember) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["memberId"],
        message: "memberId bắt buộc với incomeType='sponsorship_member'",
      });
    }
    if (val.incomeType !== INCOME_TYPE.SPONSORSHIP_MEMBER && hasMember) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["memberId"],
        message: `memberId không hợp lệ với incomeType='${val.incomeType}'`,
      });
    }
  });

export type RecordIncomeRequest = z.infer<typeof recordIncomeRequestSchema>;

export const recordIncomeResponseSchema = z
  .object({
    /** Ledger tx THẬT (DEBIT CASH / CREDIT INCOME) — bằng chứng producer, không phải enum chết. */
    ledgerTxId: z.string().uuid(),
    fundId: z.string().uuid(),
    incomeType: z.enum([
      INCOME_TYPE.SPONSORSHIP_MEMBER,
      INCOME_TYPE.SPONSORSHIP_EXTERNAL,
      INCOME_TYPE.OTHER_INCOME,
    ]),
    txType: z.enum(["SPONSORSHIP_MEMBER", "SPONSORSHIP_EXTERNAL", "OTHER_INCOME"]),
    amountMinor: z.string().regex(INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    memberId: z.string().uuid().nullable(),
    sourceRef: z.string().nullable(),
    recordedAt: z.string().datetime(),
  })
  .strict();

export type RecordIncomeResponse = z.infer<typeof recordIncomeResponseSchema>;

/* ---------------------------------------------------------------------------
 * S02 — SPECIAL COLLECTIONS (penalty / usage / by_area) · @UC9245–@UC9247
 * ------------------------------------------------------------------------- */

/**
 * 3 kind thu đặc thù (Collection.kind — mở rộng periodic/adhoc/settlement):
 *  - penalty : thu phạt (đi trễ/vi phạm) — entries per-member, có reason.
 *  - usage   : thu theo buổi/điểm danh — due = unitAmountMinor × count (attendanceSplit).
 *  - by_area : thu theo diện tích m²/trọng số — dues = weightedSplit(totalMinor, areaWeights).
 */
export const SPECIAL_COLLECTION_KIND = {
  PENALTY: "penalty",
  USAGE: "usage",
  BY_AREA: "by_area",
} as const;

export type SpecialCollectionKind =
  (typeof SPECIAL_COLLECTION_KIND)[keyof typeof SPECIAL_COLLECTION_KIND];

/** Số buổi điểm danh — nguyên ≥ 0 (0 ⇒ KHÔNG sinh due). */
const attendanceCount = z.number().int().min(0).max(MAX_WEIGHT);
/** Trọng số m² — nguyên > 0 (0/âm ⇒ reject; ai có due phải có diện tích). */
const areaWeight = z.number().int().positive().max(MAX_WEIGHT);

const penaltyEntrySchema = z
  .object({ groupMemberId: memberIdField, amountMinor: amountMinorString })
  .strict();

/**
 * POST /v1/funds/:id/collections/special — tạo đợt thu đặc thù. Object đơn `.strict()` +
 * `.superRefine` (tương thích createZodDto nestjs-zod) — field của kind khác ⇒ reject (chống nhầm
 * payload / over-post). currency kế thừa fund; status/kind do server set.
 */
export const createSpecialCollectionRequestSchema = z
  .object({
    title: titleField,
    kind: z.enum([
      SPECIAL_COLLECTION_KIND.PENALTY,
      SPECIAL_COLLECTION_KIND.USAGE,
      SPECIAL_COLLECTION_KIND.BY_AREA,
    ]),
    dueDate: dueDateField,
    /** penalty: lý do phạt (đi vào memo ledger lúc record-paid — minh bạch "vì sao thu"). */
    reason: z.string().trim().min(1).max(REASON_MAX).optional(),
    /** penalty: danh sách member bị phạt + số tiền. */
    entries: z.array(penaltyEntrySchema).min(1).max(MAX_ENTRIES).optional(),
    /** usage: đơn giá 1 buổi. */
    unitAmountMinor: amountMinorString.optional(),
    /** usage: map member → số buổi (0 ⇒ không due). */
    attendanceMap: z.record(memberIdField, attendanceCount).optional(),
    /** by_area: TỔNG cần chia. */
    totalMinor: amountMinorString.optional(),
    /** by_area: map member → m² (trọng số). */
    areaWeights: z.record(memberIdField, areaWeight).optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    const require = (present: boolean, path: string) => {
      if (!present) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path],
          message: `${path} bắt buộc với kind='${val.kind}'`,
        });
      }
    };
    const forbid = (present: boolean, path: string) => {
      if (present) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path],
          message: `${path} không hợp lệ với kind='${val.kind}'`,
        });
      }
    };

    const hasEntries = val.entries !== undefined;
    const hasUnit = val.unitAmountMinor !== undefined;
    const hasAttendance = val.attendanceMap !== undefined;
    const hasTotal = val.totalMinor !== undefined;
    const hasWeights = val.areaWeights !== undefined;

    switch (val.kind) {
      case SPECIAL_COLLECTION_KIND.PENALTY:
        require(hasEntries, "entries");
        forbid(hasUnit, "unitAmountMinor");
        forbid(hasAttendance, "attendanceMap");
        forbid(hasTotal, "totalMinor");
        forbid(hasWeights, "areaWeights");
        break;
      case SPECIAL_COLLECTION_KIND.USAGE:
        require(hasUnit, "unitAmountMinor");
        require(hasAttendance, "attendanceMap");
        forbid(hasEntries, "entries");
        forbid(hasTotal, "totalMinor");
        forbid(hasWeights, "areaWeights");
        break;
      case SPECIAL_COLLECTION_KIND.BY_AREA:
        require(hasTotal, "totalMinor");
        require(hasWeights, "areaWeights");
        forbid(hasEntries, "entries");
        forbid(hasUnit, "unitAmountMinor");
        forbid(hasAttendance, "attendanceMap");
        break;
    }
  });

export type CreateSpecialCollectionRequest = z.infer<
  typeof createSpecialCollectionRequestSchema
>;

export const specialCollectionResponseSchema = z
  .object({
    collectionId: z.string().uuid(),
    fundId: z.string().uuid(),
    title: z.string(),
    kind: z.enum([
      SPECIAL_COLLECTION_KIND.PENALTY,
      SPECIAL_COLLECTION_KIND.USAGE,
      SPECIAL_COLLECTION_KIND.BY_AREA,
    ]),
    currency: z.string().length(3),
    dueDate: z.string().regex(ISO_DATE_PATTERN),
    duesCreated: z.number().int().nonnegative(),
    /** Σ due sinh ra (STRING minor — B1). by_area: PHẢI = totalMinor (P1.3). */
    totalDueMinor: z.string().regex(INTEGER_STRING_PATTERN),
    /** by_area: member gánh đồng lẻ (đồng lẻ CÓ TÊN); kind khác = null. */
    remainderBearerMemberId: z.string().uuid().nullable(),
  })
  .strict();

export type SpecialCollectionResponse = z.infer<typeof specialCollectionResponseSchema>;
